import { useState, useCallback, useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTenant } from '@/contexts/TenantProvider'
import { useSectionContent, useGlobalSave } from '@/hooks/use-site-content'
import { getSectionDef } from '@/lib/cms/section-fields'
import type { SectionField } from '@/lib/cms/section-fields'
import ImageUploader from '@/components/cms/ImageUploader'
import { Check, ExternalLink, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'

/* ============================================================
   SectionContentEditor — Inline content editing for sections
   Auto-saves on blur so edits persist when the user clicks away.
   Exposes flush() via ref so parent can trigger save before publish.
   ============================================================ */

export interface SectionContentEditorHandle {
  flush: () => void
  isDirty: boolean
}

interface SectionContentEditorProps {
  sectionKey: string
}

/** Map entity types to their admin page paths */
const ENTITY_ROUTES: Record<string, { label: string; path: string }> = {
  services: { label: 'Manage Services', path: 'services' },
  stylists: { label: 'Manage Team', path: 'stylists' },
  testimonials: { label: 'Manage Testimonials', path: 'testimonials' },
  events: { label: 'Manage Events', path: 'events' },
}

/** Group about section fields into collapsible subsections */
const ABOUT_GROUPS = [
  { id: 'philosophy', label: 'Philosophy', prefix: 'philosophy_' },
  { id: 'founder', label: 'Founder', prefix: 'founder_' },
  { id: 'approach', label: 'Approach', prefix: 'approach_' },
]

const SectionContentEditor = forwardRef<SectionContentEditorHandle, SectionContentEditorProps>(
  function SectionContentEditor({ sectionKey }, ref) {
    const { tenant } = useTenant()
    const { slug } = useParams()
    const tenantId = tenant?.id
    const def = getSectionDef(sectionKey)
    const { data: savedContent = {}, isLoading } = useSectionContent(tenantId, sectionKey)
    const globalSave = useGlobalSave()

    // Local form state — initialized from saved content
    const [formValues, setFormValues] = useState<Record<string, string>>({})
    const [isDirty, setIsDirty] = useState(false)
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['philosophy']))
    const dirtyRef = useRef(isDirty)
    const formRef = useRef(formValues)
    dirtyRef.current = isDirty
    formRef.current = formValues

    // Sync local state when saved content loads
    useEffect(() => {
      if (savedContent && Object.keys(savedContent).length > 0) {
        setFormValues(savedContent)
        setIsDirty(false)
      }
    }, [savedContent])

    const handleChange = useCallback((key: string, value: string) => {
      setFormValues((prev) => ({ ...prev, [key]: value }))
      setIsDirty(true)
      setSaveStatus('idle')
    }, [])

    /** Persist current form values to Supabase */
    const flush = useCallback(() => {
      if (!tenantId || !dirtyRef.current) return

      const changes = Object.entries(formRef.current).map(([content_key, content_value]) => ({
        tenant_id: tenantId,
        section: sectionKey,
        content_key,
        content_value,
      }))

      setSaveStatus('saving')
      globalSave.mutate(changes, {
        onSuccess: () => {
          setIsDirty(false)
          setSaveStatus('saved')
          setTimeout(() => setSaveStatus('idle'), 2500)
        },
        onError: () => {
          setSaveStatus('idle')
        },
      })
    }, [tenantId, sectionKey, globalSave])

    // Auto-save on blur (when user clicks away from a field)
    const handleBlur = useCallback(() => {
      // Small delay to allow React state to flush before saving
      setTimeout(() => flush(), 50)
    }, [flush])

    // Expose flush to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        flush,
        get isDirty() {
          return dirtyRef.current
        },
      }),
      [flush]
    )

    const toggleGroup = useCallback((groupId: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev)
        if (next.has(groupId)) next.delete(groupId)
        else next.add(groupId)
        return next
      })
    }, [])

    if (!def) return null

    const hasFields = def.fields && def.fields.length > 0
    const hasEntity = !!def.entityType

    // For the about section, group fields into subsections
    const isAboutSection = sectionKey === 'about'

    return (
      <div className="border-t border-zinc-200 dark:border-white/[0.04] bg-zinc-50/50 dark:bg-surface/[0.01] px-6 py-4 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-lg animate-pulse bg-zinc-100 dark:bg-surface/[0.03]"
              />
            ))}
          </div>
        ) : (
          <>
            {/* Content fields — standard sections */}
            {hasFields && !isAboutSection && (
              <div className="space-y-3">
                {def.fields!.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={formValues[field.key] ?? ''}
                    onChange={(val: string) => handleChange(field.key, val)}
                    onBlur={handleBlur}
                    tenantId={tenantId!}
                    sectionKey={sectionKey}
                  />
                ))}
              </div>
            )}

            {/* Content fields — about section with collapsible groups */}
            {hasFields && isAboutSection && (
              <AboutSectionGroups
                fields={def.fields!}
                formValues={formValues}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                onChange={handleChange}
                onBlur={handleBlur}
                tenantId={tenantId!}
                sectionKey={sectionKey}
              />
            )}

            {/* Entity link — for entity-backed sections */}
            {hasEntity && def.entityType && (
              <div className="flex items-center gap-2">
                {ENTITY_ROUTES[def.entityType] && (
                  <Link
                    to={`/${slug}/admin/${ENTITY_ROUTES[def.entityType].path}`}
                    className="flex items-center gap-1.5 text-[11px] font-mono px-3 py-2 rounded-lg bg-zinc-50 dark:bg-surface/[0.03] border border-zinc-200 dark:border-white/[0.08] text-origin-accent hover:bg-zinc-100 dark:hover:bg-surface/[0.06] transition-colors"
                  >
                    <ExternalLink size={11} />
                    {ENTITY_ROUTES[def.entityType].label} →
                  </Link>
                )}
              </div>
            )}

            {/* Auto-save status indicator */}
            {hasFields && (
              <div className="flex items-center gap-2 pt-2 border-t border-zinc-200 dark:border-white/[0.04]">
                {saveStatus === 'saving' && (
                  <span className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-400 dark:text-white/40">
                    <Loader2 size={10} className="animate-spin" />
                    Saving…
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-400/60">
                    <Check size={10} />
                    Saved
                  </span>
                )}
                {isDirty && saveStatus === 'idle' && (
                  <span className="text-[10px] font-mono text-zinc-400 dark:text-white/25">
                    Changes auto-save when you click away
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  }
)

export default SectionContentEditor

/* ────────────────────────────────────────────────────────────
   AboutSectionGroups — collapsible subsections for the about section
   ──────────────────────────────────────────────────────────── */

function AboutSectionGroups({
  fields,
  formValues,
  expandedGroups,
  onToggleGroup,
  onChange,
  onBlur,
  tenantId,
  sectionKey,
}: {
  fields: SectionField[]
  formValues: Record<string, string>
  expandedGroups: Set<string>
  onToggleGroup: (id: string) => void
  onChange: (key: string, value: string) => void
  onBlur: () => void
  tenantId: string
  sectionKey: string
}) {
  return (
    <div className="space-y-2">
      {ABOUT_GROUPS.map((group) => {
        const groupFields = fields.filter((f) => f.key.startsWith(group.prefix))
        if (groupFields.length === 0) return null
        const isExpanded = expandedGroups.has(group.id)
        return (
          <div
            key={group.id}
            className="rounded-lg border border-zinc-200 dark:border-white/[0.06] overflow-hidden"
          >
            <button
              type="button"
              onClick={() => onToggleGroup(group.id)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-zinc-50 dark:bg-surface/[0.02] hover:bg-zinc-100 dark:hover:bg-surface/[0.04] transition-colors text-left"
            >
              {isExpanded ? (
                <ChevronDown size={12} className="text-zinc-400 dark:text-white/40" />
              ) : (
                <ChevronRight size={12} className="text-zinc-400 dark:text-white/40" />
              )}
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500 dark:text-white/50">
                {group.label}
              </span>
              <span className="text-[10px] font-mono text-zinc-400 dark:text-white/20 ml-auto">
                {groupFields.length} fields
              </span>
            </button>
            {isExpanded && (
              <div className="px-4 py-3 space-y-3 border-t border-zinc-200 dark:border-white/[0.04]">
                {groupFields.map((field) => (
                  <FieldInput
                    key={field.key}
                    field={field}
                    value={formValues[field.key] ?? ''}
                    onChange={(val: string) => onChange(field.key, val)}
                    onBlur={onBlur}
                    tenantId={tenantId}
                    sectionKey={sectionKey}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ────────────────────────────────────────────────────────────
   FieldInput — Renders the appropriate input for a field type
   ──────────────────────────────────────────────────────────── */

interface FieldInputProps {
  field: SectionField
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  tenantId: string
  sectionKey: string
}

function FieldInput({ field, value, onChange, onBlur, tenantId, sectionKey }: FieldInputProps) {
  if (field.type === 'image') {
    return (
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-white/40 mb-1.5">
          {field.label}
        </label>
        <ImageUploader
          tenantId={tenantId}
          section={sectionKey}
          fieldKey={field.key}
          currentUrl={value || undefined}
          onUpload={(url) => onChange(url)}
        />
      </div>
    )
  }

  if (field.type === 'textarea' || field.type === 'html') {
    return (
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-white/40 mb-1.5">
          {field.label}
        </label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          rows={field.type === 'html' ? 4 : 3}
          className={`w-full px-3 py-2 rounded-lg bg-surface dark:bg-surface/[0.03] border border-zinc-200 dark:border-white/[0.08] text-sm text-text-primary placeholder-zinc-300 dark:placeholder-white/20 resize-y focus:outline-none focus:border-origin-accent/30 transition-colors ${
            field.type === 'html' ? 'font-mono text-xs' : ''
          }`}
        />
        {field.maxLength && (
          <div className="flex justify-end mt-0.5">
            <span
              className={`text-[9px] font-mono ${value.length > field.maxLength * 0.9 ? 'text-status-warning-text dark:text-amber-400/60' : 'text-zinc-300 dark:text-white/15'}`}
            >
              {value.length}/{field.maxLength}
            </span>
          </div>
        )}
      </div>
    )
  }

  // text, url, email
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 dark:text-white/40 mb-1.5">
        {field.label}
      </label>
      <input
        type={field.type === 'url' ? 'url' : field.type === 'email' ? 'email' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        className="w-full px-3 py-2 rounded-lg bg-surface dark:bg-surface/[0.03] border border-zinc-200 dark:border-white/[0.08] text-sm text-text-primary placeholder-zinc-300 dark:placeholder-white/20 focus:outline-none focus:border-origin-accent/30 transition-colors"
      />
      {field.maxLength && (
        <div className="flex justify-end mt-0.5">
          <span
            className={`text-[9px] font-mono ${value.length > field.maxLength * 0.9 ? 'text-status-warning-text dark:text-amber-400/60' : 'text-zinc-300 dark:text-white/15'}`}
          >
            {value.length}/{field.maxLength}
          </span>
        </div>
      )}
    </div>
  )
}
